import React from "react";
import { FaRedo, FaSearch, FaTimes } from "react-icons/fa";
import { FilterSelect } from "./MarketplaceBrowseUi";

export default function ContractManufacturersFiltersPanel({
  filters,
  onChange,
  onClear,
  stateFilterOptions = [],
  cityFilterOptions = [],
  cmTypeFilterOptions = [],
  facilityTypeFilterOptions = [],
  sidebarIndustryOptions = [],
  sidebarCertFilterOptions = [],
  industrySlug,
  searchPlaceholder = "Search in this industry…",
  searchLabel = "Search Manufacturer",
  industrySummaryLabel,
}) {
  const updateFilter = (key, value) => {
    onChange?.({ ...filters, [key]: value });
  };

  const toggleIndustryFilter = (value) => {
    const current = Array.isArray(filters.selectedIndustryFilters) ? filters.selectedIndustryFilters : [];
    const next = current.includes(value) ? current.filter((entry) => entry !== value) : [...current, value];
    updateFilter("selectedIndustryFilters", next);
  };

  const toggleCertFilter = (value) => {
    const current = Array.isArray(filters.selectedCertFilters) ? filters.selectedCertFilters : [];
    const next = current.includes(value) ? current.filter((entry) => entry !== value) : [...current, value];
    updateFilter("selectedCertFilters", next);
  };

  const industrySectionLabel =
    industrySummaryLabel || (industrySlug ? "Additional Industries" : "Industry Type");

  return (
    <aside className="product-marketplace-filters">
      <div className="product-marketplace-filters__head">
        <h2>Filters</h2>
        <button type="button" onClick={onClear}>
          Clear All
        </button>
      </div>

      <div className="product-marketplace-filters__field">
        <label htmlFor="cm-search">{searchLabel}</label>
        <div className={`product-marketplace-filters__search-wrap${filters.search ? " has-value" : ""}`}>
          <FaSearch className="product-marketplace-filters__search-icon" aria-hidden />
          <input
            id="cm-search"
            type="search"
            className="product-marketplace-filters__search-input"
            placeholder={searchPlaceholder}
            value={filters.search || ""}
            onChange={(e) => updateFilter("search", e.target.value)}
          />
          {filters.search ? (
            <button
              type="button"
              className="product-marketplace-filters__clear"
              aria-label="Clear search"
              onClick={() => updateFilter("search", "")}
            >
              <FaTimes aria-hidden />
            </button>
          ) : null}
        </div>
      </div>

      <FilterSelect
        id="cm-state"
        label="Location (State)"
        value={filters.selectedStateFilter || ""}
        placeholder="All States"
        options={stateFilterOptions}
        onChange={(e) => updateFilter("selectedStateFilter", e.target.value)}
        onClear={() => updateFilter("selectedStateFilter", "")}
      />

      <FilterSelect
        id="cm-city"
        label="City"
        value={filters.selectedCityFilter || ""}
        placeholder="All Cities"
        options={cityFilterOptions}
        onChange={(e) => updateFilter("selectedCityFilter", e.target.value)}
        onClear={() => updateFilter("selectedCityFilter", "")}
      />

      <FilterSelect
        id="cm-manufacturing-type"
        label="Contract Manufacturing Type"
        value={filters.selectedCmTypeFilter || ""}
        placeholder="All Types"
        options={cmTypeFilterOptions}
        onChange={(e) => updateFilter("selectedCmTypeFilter", e.target.value)}
        onClear={() => updateFilter("selectedCmTypeFilter", "")}
      />

      <FilterSelect
        id="cm-facility-type"
        label="Facility Type"
        value={filters.selectedFacilityTypeFilter || ""}
        placeholder="All Facility Types"
        options={facilityTypeFilterOptions}
        onChange={(e) => updateFilter("selectedFacilityTypeFilter", e.target.value)}
        onClear={() => updateFilter("selectedFacilityTypeFilter", "")}
      />

      <details className="product-marketplace-filters__accordion" open>
        <summary>Review Rating</summary>
        <div className="product-marketplace-filters__radio-list">
          <label>
            <input
              type="radio"
              name="cm-rating-filter"
              checked={filters.ratingFilter == null || filters.ratingFilter === ""}
              onChange={() => updateFilter("ratingFilter", null)}
            />
            Any rating
          </label>
          {[5, 4, 3, 2, 1].map((rating) => (
            <label key={rating}>
              <input
                type="radio"
                name="cm-rating-filter"
                checked={filters.ratingFilter === rating}
                onChange={() => updateFilter("ratingFilter", rating)}
              />
              {rating === 5 ? "5 stars & up" : `${rating} stars & up`}
            </label>
          ))}
        </div>
      </details>

      {sidebarIndustryOptions.length > 0 ? (
        <details className="product-marketplace-filters__accordion">
          <summary>{industrySectionLabel}</summary>
          <p className="product-marketplace-filters__hint">
            {industrySlug
              ? "Match manufacturers that also serve the selected industries."
              : "Match any selected industry."}
          </p>
          <div className="product-marketplace-filters__checklist">
            {sidebarIndustryOptions.map((opt) => (
              <label key={opt.value} className="product-marketplace-filters__check">
                <input
                  type="checkbox"
                  checked={(filters.selectedIndustryFilters || []).includes(opt.value)}
                  onChange={() => toggleIndustryFilter(opt.value)}
                />
                <span>{opt.label}</span>
              </label>
            ))}
          </div>
        </details>
      ) : null}

      {sidebarCertFilterOptions.length > 0 ? (
        <details className="product-marketplace-filters__accordion">
          <summary>Quality Certifications</summary>
          <p className="product-marketplace-filters__hint">Match any selected certification.</p>
          <div className="product-marketplace-filters__checklist">
            {sidebarCertFilterOptions.map((opt) => (
              <label key={opt.value} className="product-marketplace-filters__check">
                <input
                  type="checkbox"
                  checked={(filters.selectedCertFilters || []).includes(opt.value)}
                  onChange={() => toggleCertFilter(opt.value)}
                />
                <span>{opt.label}</span>
              </label>
            ))}
          </div>
        </details>
      ) : null}

      <label className="product-marketplace-filters__check">
        <input
          type="checkbox"
          checked={Boolean(filters.verifiedOnly)}
          onChange={(e) => updateFilter("verifiedOnly", e.target.checked)}
        />
        <span>Verified manufacturers only</span>
      </label>

      <label className="product-marketplace-filters__check">
        <input
          type="checkbox"
          checked={Boolean(filters.featuredOnly)}
          onChange={(e) => updateFilter("featuredOnly", e.target.checked)}
        />
        <span>Featured manufacturers only</span>
      </label>

      <div className="product-marketplace-filters__actions">
        <button type="button" className="product-marketplace-btn product-marketplace-btn--outline" onClick={onClear}>
          <FaRedo aria-hidden /> Reset Filters
        </button>
      </div>
    </aside>
  );
}
